import React from "react";
import { FileLock2, ShieldCheck, ExternalLink, Check, Sparkles } from "lucide-react";

const NDA_URL = "https://drive.google.com/file/d/1GEcqD05JlUHFh-4Fvy0ooVUG8oVn8HQ4/view?usp=drive_link";
const SLA_URL = "https://drive.google.com/file/d/1-c51JIAq6vkd2EiGoNtxm0MDHjR_eS9D/view?usp=drive_link";

const docs = [
  {
    key: "nda",
    badge: "NDA",
    title: "Mutual Non Disclosure Agreement",
    body:
      "Optional and entirely your call. If you choose to engage, we sign one mutual NDA at the start, and it covers every advice document I deliver for you going forward. No paperwork on a file by file basis. Built specifically for Australian financial practices, the agreement guarantees compliance with Australian Privacy Principle 8 (APP 8) for cross border data handling and strictly aligns with the Notifiable Data Breaches (NDB) scheme. Zero local data retention, ever.",
    points: ["One NDA. Whole engagement.", "APP 8 and NDB scheme aligned", "Zero local data retention"],
    cta: "Review Sample NDA",
    href: NDA_URL,
    icon: FileLock2,
    tone: "white",
  },
  {
    key: "sla",
    badge: "SLA",
    title: "Service Level Agreement",
    body:
      "Also optional. If you want it locked in writing, we sign one SLA at the start of the engagement and it governs every SOA, ROA and review I deliver for you. It outlines the Approval First billing model, guaranteed turnaround times for standard ROAs and complex SMSF or Trust SOAs, and a strict policy against hidden retainer fees. You always know exactly what you are paying for, and exactly when it will land.",
    points: ["One SLA. Whole engagement.", "Approval First billing built in", "Zero hidden retainer fees"],
    cta: "Review Sample SLA",
    href: SLA_URL,
    icon: ShieldCheck,
    tone: "yellow",
  },
];

const Compliance = () => {
  return (
    <section
      id="compliance"
      data-testid="compliance-section"
      className="relative bg-white border-b-2 border-black"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="max-w-3xl">
          <span className="inline-block bg-[#FACC15] border-2 border-black px-3 py-1 text-xs font-bold uppercase tracking-widest shadow-brutal">
            Engagement Terms
          </span>
          <h2 className="font-display font-black mt-4 text-4xl md:text-5xl tracking-tighter leading-[1.05]">
            One NDA. One SLA. The entire engagement.
          </h2>
          <p className="mt-4 text-base md:text-lg text-black/70 italic">
            Optional documents, at your discretion. Not a fresh signature on every file.
          </p>
          <p className="mt-5 text-base md:text-lg text-black/80 leading-relaxed">
            Outsourcing your paraplanning should not mean a stack of fresh paperwork for every SOA.
            If you decide to engage me as your contract paraplanner, we sign one NDA and one SLA at
            the start. Those documents govern every advice file I touch for you going forward.
            Standard ROAs, complex SMSF SOAs, ongoing reviews. One set of terms. Full engagement.
          </p>
        </div>

        <div className="mt-10 inline-flex items-start gap-3 bg-black text-white border-2 border-black px-5 py-4 shadow-brutal-yellow max-w-3xl" data-testid="compliance-engagement-banner">
          <Sparkles className="w-5 h-5 text-[#FACC15] mt-0.5 shrink-0" />
          <p className="text-sm md:text-base leading-relaxed">
            <strong className="text-[#FACC15]">Your call.</strong> Some practices prefer to skip
            paperwork and start with a single trial SOA. Others want everything locked down before
            file one. Either way, you decide. If you want either or both documents in place, one
            signature at the start of our engagement is all it takes.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-6 md:gap-8">
          {docs.map((d) => {
            const Icon = d.icon;
            const isYellow = d.tone === "yellow";
            return (
              <article
                key={d.key}
                data-testid={`compliance-${d.key}`}
                className={`relative border-2 border-black p-8 md:p-10 hover-lift ${
                  isYellow ? "bg-[#FACC15]" : "bg-white"
                } shadow-brutal-lg`}
              >
                <div className="flex items-start justify-between">
                  <span className="inline-flex items-center justify-center w-12 h-12 border-2 border-black bg-black">
                    <Icon className="w-6 h-6 text-[#FACC15]" />
                  </span>
                  <span className="inline-block bg-black text-white px-3 py-1 text-xs font-bold uppercase tracking-widest border-2 border-black">
                    {d.badge}
                  </span>
                </div>
                <h3 className="font-display font-black mt-6 text-2xl md:text-3xl tracking-tight leading-tight">{d.title}</h3>
                <p className="mt-3 text-sm md:text-base text-black/80 leading-relaxed">{d.body}</p>
                <ul className="mt-5 space-y-2">
                  {d.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm font-semibold">
                      <span className={`shrink-0 mt-0.5 w-5 h-5 border-2 border-black flex items-center justify-center ${isYellow ? "bg-white" : "bg-[#FACC15]"}`}>
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
                <a
                  href={d.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`compliance-cta-${d.key}`}
                  className="mt-7 inline-flex items-center gap-2 bg-black text-white border-2 border-black font-bold uppercase text-sm tracking-wide py-3 px-5 hover:bg-white hover:text-black transition-colors"
                >
                  {d.cta} <ExternalLink className="w-4 h-4" />
                </a>
              </article>
            );
          })}
        </div>

        <p className="mt-8 text-xs md:text-sm text-black/60 italic max-w-3xl">
          All agreements are provided in good faith as standard templates. Final agreements are
          executed prior to commencement of the engagement, only if you choose to put them in
          place.
        </p>
      </div>
    </section>
  );
};

export default Compliance;
